import { Edit2, Loader2, Send } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SectionTitle } from '../components/forms/FormComponents';
import { ALL_LOCATION_OPTIONS, ROLE_OPTIONS, SHIFT_OPTIONS, mapOptionValues } from '../lib/availabilityOptions';
import { formatDisplayValue } from '../lib/previewFormat';
import { toast } from '../lib/toast';
import { getFirstStepIndexForMessages, mapValidationMessages } from '../lib/validationMessages';
import { validateCrossStepRules, validateStep } from '../lib/validations';
import { useApplicationStore } from '../store/applicationStore';

interface WizardStep {
  key: 'stepA' | 'stepB' | 'stepC' | 'stepD' | 'stepE' | 'stepF';
  label: string;
}

const PREVIEW_STEPS: WizardStep[] = [
  { key: 'stepA', label: 'wizard.steps.personal' },
  { key: 'stepB', label: 'wizard.steps.eligibility' },
  { key: 'stepC', label: 'wizard.steps.licensing' },
  { key: 'stepD', label: 'wizard.steps.availability' },
  { key: 'stepE', label: 'wizard.steps.preferences' },
  { key: 'stepF', label: 'wizard.steps.consents' },
];

function formatAddress(address?: string, city?: string, state?: string, zip?: string): string | null {
  const parts = [address, city, state, zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function PreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const data = useApplicationStore((state) => state.data);
  const isSaving = useApplicationStore((state) => state.isSaving);
  const isLoading = useApplicationStore((state) => state.isLoading);
  const serverApplicationId = useApplicationStore((state) => state.serverApplicationId);
  const loadDraft = useApplicationStore((state) => state.loadDraft);
  const submitApplication = useApplicationStore((state) => state.submitApplication);
  const goToStep = useApplicationStore((state) => state.goToStep);

  const draftLoadAttempted = useRef(false);

  const stepLabels = useMemo(() => PREVIEW_STEPS.map((step) => ({ ...step, label: t(step.label) })), [t]);

  useEffect(() => {
    if (draftLoadAttempted.current) return;
    draftLoadAttempted.current = true;

    if (serverApplicationId) return;

    loadDraft().catch(() => {
      toast.error(t('wizard.errors.loadFailed'));
    });
  }, [loadDraft, serverApplicationId, t]);

  const handleEdit = (stepIndex: number) => {
    goToStep(stepIndex);
    void navigate('/wizard', { state: { step: stepIndex } });
  };

  const handleSubmit = async () => {
    for (const [index, step] of stepLabels.entries()) {
      const result = validateStep(step.key, data);
      if (!result.success) {
        toast.error(t('wizard.preview.fixStep', { step: step.label }));
        goToStep(index);
        void navigate('/wizard', { state: { step: index, errors: result.errors } });
        return;
      }
    }

    const crossStepErrors = validateCrossStepRules(data);
    if (crossStepErrors.length > 0) {
      const stepIndex = getFirstStepIndexForMessages(crossStepErrors) ?? 0;
      toast.error(t('wizard.validation.fixErrors', { count: crossStepErrors.length }));
      goToStep(stepIndex);
      void navigate('/wizard', {
        state: { step: stepIndex, errors: mapValidationMessages(crossStepErrors) },
      });
      return;
    }

    try {
      const result = await submitApplication();

      if (!result.success && result.errors) {
        const stepIndex = getFirstStepIndexForMessages(result.errors) ?? 0;
        toast.error(t('wizard.validation.fixErrors', { count: result.errors.length }));
        goToStep(stepIndex);
        void navigate('/wizard', {
          state: { step: stepIndex, errors: mapValidationMessages(result.errors) },
        });
        return;
      }

      if (result.warnings) {
        for (const warning of result.warnings) {
          toast.warning(warning.message);
        }
      }

      toast.success(t('wizard.toasts.submitted'));
      void navigate('/success');
    } catch {
      toast.error(t('wizard.errors.saveFailed'));
    }
  };

  const yesNoLabels = useMemo(() => ({ yesLabel: t('common.yes'), noLabel: t('common.no') }), [t]);

  const roleLabels = useMemo(() => mapOptionValues(data.stepD.roles, ROLE_OPTIONS), [data.stepD.roles]);
  const locationLabels = useMemo(
    () => mapOptionValues(data.stepD.locations, ALL_LOCATION_OPTIONS),
    [data.stepD.locations],
  );
  const shiftLabels = useMemo(() => mapOptionValues(data.stepD.shifts, SHIFT_OPTIONS), [data.stepD.shifts]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  return (
    <div className='max-w-3xl mx-auto p-4 pb-24'>
      <SectionTitle title={t('wizard.preview.title')} subtitle={t('wizard.preview.subtitle')} />

      <div className='space-y-4'>
        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.personal')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(0)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            <PreviewRow label={t('wizard.stepA.firstName')} value={data.stepA.firstName} />
            <PreviewRow label={t('wizard.stepA.middleName')} value={data.stepA.middleName} />
            <PreviewRow label={t('wizard.stepA.lastName')} value={data.stepA.lastName} />
            <PreviewRow label={t('wizard.stepA.preferredName')} value={data.stepA.preferredName} />
            <PreviewRow label={t('wizard.stepA.email')} value={data.stepA.email} />
            <PreviewRow label={t('wizard.stepA.phone')} value={data.stepA.phone} />
            <PreviewRow
              label={t('wizard.stepA.address')}
              value={formatAddress(data.stepA.address, data.stepA.city, data.stepA.state, data.stepA.zip)}
            />
            <PreviewRow label={t('wizard.stepA.language')} value={data.stepA.language} />
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.eligibility')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(1)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            <PreviewRow
              label={t('wizard.stepB.ageConfirmation')}
              value={formatDisplayValue(data.stepB.isOver18, yesNoLabels)}
            />
            <PreviewRow
              label={t('wizard.stepB.workAuthorization')}
              value={formatDisplayValue(data.stepB.isAuthorizedToWork, yesNoLabels)}
            />
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.licensing')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(2)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            {data.stepA.state === 'FL' ? (
              <>
                <PreviewRow
                  label={t('wizard.stepC.hasClassD')}
                  value={formatDisplayValue(data.stepC.florida?.hasClassD ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.licenseNumber')} value={data.stepC.florida?.classDNumber} />
                <PreviewRow label={t('wizard.stepC.expirationDate')} value={data.stepC.florida?.classDExpiration} />
                <PreviewRow
                  label={t('wizard.stepC.hasClassG')}
                  value={formatDisplayValue(data.stepC.florida?.hasClassG ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.licenseNumber')} value={data.stepC.florida?.classGNumber} />
                <PreviewRow label={t('wizard.stepC.expirationDate')} value={data.stepC.florida?.classGExpiration} />
              </>
            ) : null}

            {data.stepA.state === 'NY' ? (
              <>
                <PreviewRow
                  label={t('wizard.stepC.hasGuardCard')}
                  value={formatDisplayValue(data.stepC.newYork?.hasGuardCard ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.cardNumber')} value={data.stepC.newYork?.guardCardNumber} />
                <PreviewRow label={t('wizard.stepC.expirationDate')} value={data.stepC.newYork?.guardCardExpiration} />
                <PreviewRow
                  label={t('wizard.stepC.has8HourCert')}
                  value={formatDisplayValue(data.stepC.newYork?.has8HourCert ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.completionDate')} value={data.stepC.newYork?.cert8HourDate} />
                <PreviewRow
                  label={t('wizard.stepC.has16HourCert')}
                  value={formatDisplayValue(data.stepC.newYork?.has16HourCert ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.completionDate')} value={data.stepC.newYork?.cert16HourDate} />
                <PreviewRow
                  label={t('wizard.stepC.hasFirearmCreds')}
                  value={formatDisplayValue(data.stepC.newYork?.hasFirearmCreds ?? false, yesNoLabels)}
                />
                <PreviewRow label={t('wizard.stepC.credentialNumber')} value={data.stepC.newYork?.firearmCredsNumber} />
                <PreviewRow
                  label={t('wizard.stepC.expirationDate')}
                  value={data.stepC.newYork?.firearmCredsExpiration}
                />
              </>
            ) : null}

            <div className='pt-2 border-t border-gray-100'>
              <PreviewRow
                label={t('wizard.stepC.hasCprAed')}
                value={formatDisplayValue(data.stepC.certifications?.hasCprAed ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('wizard.stepC.expirationDate')}
                value={data.stepC.certifications?.cprAedExpiration}
              />
              <PreviewRow
                label={t('wizard.stepC.hasTwic')}
                value={formatDisplayValue(data.stepC.certifications?.hasTwic ?? false, yesNoLabels)}
              />
              <PreviewRow label={t('wizard.stepC.expirationDate')} value={data.stepC.certifications?.twicExpiration} />
            </div>
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.availability')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(3)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            <PreviewRow label={t('wizard.stepD.desiredRoles')} value={formatDisplayValue(roleLabels)} />
            <PreviewRow label={t('wizard.stepD.locationPreferences')} value={formatDisplayValue(locationLabels)} />
            <PreviewRow label={t('wizard.stepD.shiftPreferences')} value={formatDisplayValue(shiftLabels)} />
            <PreviewRow
              label={t('wizard.stepD.reliableTransport')}
              value={formatDisplayValue(data.stepD.hasTransport, yesNoLabels)}
            />
            <PreviewRow label={t('wizard.stepD.earliestStartDate')} value={data.stepD.startDate} />
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.preferences')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(4)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            <PreviewRow
              label={t('wizard.stepE.indoorOutdoorComfort')}
              value={formatDisplayValue(data.stepE.indoorOutdoorComfort)}
            />
            <PreviewRow
              label={t('wizard.stepE.standingPatrolTolerance')}
              value={formatDisplayValue(data.stepE.standingPatrolTolerance)}
            />
            <PreviewRow
              label={t('wizard.stepE.languagesSpoken')}
              value={formatDisplayValue(data.stepE.languagesSpoken)}
            />
            <PreviewRow
              label={t('wizard.stepE.veteranStatus')}
              value={formatDisplayValue(data.stepE.isVeteran, yesNoLabels)}
            />
            <PreviewRow
              label={t('wizard.stepE.eeoConsentLabel')}
              value={formatDisplayValue(data.stepE.eeoDisclosureConsent, yesNoLabels)}
            />
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-5'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='font-bold text-xl text-gray-800'>{t('wizard.steps.consents')}</h3>
            <button
              type='button'
              onClick={() => handleEdit(5)}
              className='text-primary hover:text-primary-dark flex items-center text-sm font-medium'>
              <Edit2 size={14} className='mr-1' /> {t('wizard.preview.edit')}
            </button>
          </div>
          <div className='text-sm text-gray-600 space-y-2'>
            <PreviewRow
              label={t('wizard.stepF.consentPrivacy')}
              value={formatDisplayValue(data.stepF.consentPrivacy, yesNoLabels)}
            />
            <PreviewRow
              label={t('wizard.stepF.consentMessaging')}
              value={formatDisplayValue(data.stepF.consentMessaging, yesNoLabels)}
            />
            <PreviewRow
              label={t('wizard.stepF.consentVideoRecording')}
              value={formatDisplayValue(data.stepF.consentVideoRecording, yesNoLabels)}
            />
            <PreviewRow label={t('wizard.stepF.signatureLabel')} value={data.stepF.signature} />
            <PreviewRow label={t('wizard.stepF.signatureDate')} value={data.stepF.signatureDate} />
          </div>
        </div>
      </div>

      <div className='flex flex-col sm:flex-row justify-end gap-3 mt-6'>
        <button
          type='button'
          onClick={() => handleEdit(5)}
          className='px-8 py-3.5 border border-gray-200 text-gray-700 text-lg font-medium rounded-lg hover:bg-gray-50'>
          {t('wizard.preview.backToForm')}
        </button>
        <button
          type='button'
          onClick={() => void handleSubmit()}
          disabled={isSaving}
          className='flex items-center justify-center px-8 py-3.5 bg-primary text-white text-lg font-bold rounded-lg hover:bg-primary-dark transition shadow-md disabled:opacity-50'>
          {isSaving ? <Loader2 size={18} className='mr-2 animate-spin' /> : <Send size={18} className='mr-2' />}
          {t('wizard.preview.submit')}
        </button>
      </div>
    </div>
  );
}

function PreviewRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className='grid grid-cols-3 gap-2'>
      <span className='font-medium text-gray-500 col-span-1'>{label}</span>
      <span className='text-gray-900 col-span-2'>{value}</span>
    </div>
  );
}
